import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@app/core/i18n';

import PasswordCheck from '@ast/magma/components/icon/icons/PasswordSuccess';
import SpinnerIcon from '@ast/magma/components/icon/icons/Spinner';

import { Alert, AlertLevel } from '@ast/magma/components/alert';
import { Button } from '@ast/magma/components/button';

import { CompromisedCredentialsStatus } from '@app/queryTyping';

import { useCheckCompromisedCredentialsLazyQuery } from '../../queries/queryTyping/compromised-credentials-result';

import styles from './CompromisedCredentials.pcss';

interface ResultAlertProps {
  text: string;
  level: AlertLevel;
  onClose: () => void;
  onCloseAriaLabel: string;
}
const ResultAlert: React.FC<ResultAlertProps> = ({
  text, level, onClose, onCloseAriaLabel,
}) => (
  <Alert
    level={level}
    showClose={{
      onClose,
      'aria-label': onCloseAriaLabel,
    }}
  >
    {text}
  </Alert>
);

export const CompromisedCredentials = ({ password }: { password: string }) => {
  const [isAlertVisible, setAlertVisible] = useState(false);
  const [isCheckInitiated, setCheckInitiated] = useState(false);
  const alertContainer = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [
    checkCompromisedPassword,
    { data, loading, error },
  ] = useCheckCompromisedCredentialsLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const check = () => {
    // Reset alert visibility and set check initiated
    setCheckInitiated(true);
    if (isAlertVisible) {
      setAlertVisible(false);
    }

    checkCompromisedPassword({ variables: { getCompromisedCredentialsParams: { password } } });
  };

  useEffect(() => {
    if (isCheckInitiated && (data || error)) {
      setCheckInitiated(false);
      setAlertVisible(true);
      alertContainer.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isCheckInitiated, data, error]);

  const onAlertClose = useCallback(() => setAlertVisible(false), [setAlertVisible]);
  const alertsTypes = new Map<CompromisedCredentialsStatus, ResultAlertProps>([
    [
      CompromisedCredentialsStatus.NOTCOMPROMISED,
      {
        level: 'success',
        text: t(
          'password.check-compromised-credentials.success-alert-text|Success alert text if password is secure',
          'Your password is secure, thank you for checking.',
        ),
        onClose: onAlertClose,
        onCloseAriaLabel: t(
          'password.check-compromised-credentials.success-alert-close-button|Alert success close button',
          'Close compromised check success alert',
        ),
      },
    ],
    [
      CompromisedCredentialsStatus.COMPROMISED,
      {
        level: 'warning',
        text: t(
          'password.check-compromised-credentials.warning-alert-text|Warning alert text if password is compromised',
          'Your password appears on a list of known data breaches. We strongly encourage you to change it to keep your account secure.',
        ),
        onClose: onAlertClose,
        onCloseAriaLabel: t(
          'password.check-compromised-credentials.warning-alert-close-button|Alert warning close button',
          'Close compromised check warning alert',
        ),
      },
    ],
    [
      CompromisedCredentialsStatus.NOTCHECKED,
      {
        level: 'error',
        text: t(
          'password.check-compromised-credentials.error-alert-text|Error alert text if service is unavailable',
          'Unable to verify. Please try again later.',
        ),
        onClose: onAlertClose,
        onCloseAriaLabel: t(
          'password.check-compromised-credentials.error-alert-close-button|Alert error close button',
          'Close compromised check error alert',
        ),
      },
    ],
  ]);

  const alert = alertsTypes.get(
    !error
      ? data?.credentialsStatus.status || CompromisedCredentialsStatus.NOTCHECKED
      : CompromisedCredentialsStatus.NOTCHECKED,
  );

  return (
    <>
      <Button
        look="link"
        className={styles.compromisedCheckButton}
        disabled={!password?.trim()}
        aria-busy={loading}
        onClick={check}
        data-stable-name="CheckCompromisedButton"
        icon={loading ? <SpinnerIcon /> : <PasswordCheck />}
      >
        {t(
          'password.check-compromised-credentials.button|Button for check compromised credentials',
          'Check your password security',
        )}
      </Button>
      <div className={styles.compromisedCheckAlerts} ref={alertContainer}>
        {alert && isAlertVisible && !loading && (
          <ResultAlert
            level={alert.level}
            text={alert.text}
            onClose={alert.onClose}
            onCloseAriaLabel={alert.onCloseAriaLabel}
          />
        )}
      </div>
    </>
  );
};
